import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import Footer from '@/components/Footer';
import TestimonialsSection from '@/components/TestimonialsSection';
import ProjectGallery from '@/components/ProjectGallery';
import EnhancedQuoteCalculator from '@/components/EnhancedQuoteCalculator';
import MobileCTA from '@/components/MobileCTA';
import SEO from '@/components/SEO';
import AIReadabilityBlock from '@/components/AIReadabilityBlock';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Truck, Leaf, Award, Users } from 'lucide-react';
import sodInstallationImage from '@/assets/sod-installation-service.jpg';
import bulkMaterialsImage from '@/assets/bulk-materials-display.jpg';
import mulchVarietiesImage from '@/assets/mulch-varieties-display.jpg';
import premiumSoilImage from '@/assets/premium-soil-varieties.jpg';
import deliveryTruckImage from '@/assets/delivery-truck-service.jpg';
import finishedLandscapeImage from '@/assets/finished-landscape-project.jpg';
import grassVarietiesImage from '@/assets/grass-varieties-texas.jpg';

const HomePage = () => {
  const { language, t } = useLanguage();

  // SEO structured data for home page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "The Grass Depot LLC",
    "image": "https://thegrassdepot.com/grass-depot-logo.png",
    "telephone": "(936) 320-0763",
    "email": "info@thegrassdepot.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6920 FM 1488 RD",
      "addressLocality": "Magnolia",
      "addressRegion": "TX",
      "postalCode": "77354",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "30.1588",
      "longitude": "-95.4613"
    },
    "url": "https://thegrassdepot.com",
    "sameAs": [
      "https://www.facebook.com/thegrassdepot",
      "https://www.instagram.com/thegrassdepot"
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "16:00"
      }
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "30.1588",
        "longitude": "-95.4613"
      },
      "geoRadius": "30000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Landscaping Materials and Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Premium Sod Installation",
            "description": "Professional sod installation services with premium grass varieties"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Bulk Material Delivery",
            "description": "Rock, mulch, soil, and sand delivery services"
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "priceRange": "$$$"
  };

  const seoTitle = language === 'en' 
    ? 'The Grass Depot - Premium Sod, Rock, Mulch & Soil Delivery Texas'
    : 'The Grass Depot - Entrega Premium de Césped, Roca, Mantillo y Tierra Texas';
  
  const seoDescription = language === 'en'
    ? 'Texas\' premier landscaping materials supplier. Professional sod installation, bulk rock/mulch/soil delivery in Montgomery County. Free quotes, delivery available throughout Montgomery County. Call (936) 320-0763.'
    : 'Proveedor premier de materiales de paisajismo de Texas. Instalación profesional de césped, entrega a granel de roca/mantillo/tierra en el Condado de Montgomery. Cotizaciones gratis, entrega el mismo día disponible. Llama (936) 320-0763.';

  const seoKeywords = language === 'en'
    ? 'Texas landscaping materials, sod installation Montgomery County, bulk delivery Texas, rock mulch soil delivery, The Woodlands landscaping, Cypress lawn care, Texas lawn installation'
    : 'materiales paisajismo Texas, instalación césped Condado Montgomery, entrega granel Texas, entrega roca mantillo tierra, paisajismo The Woodlands, cuidado césped Cypress, instalación césped Texas';

  const features = [
    {
      icon: Truck,
      title: 'Fast Delivery',
      description: 'Delivery available throughout Montgomery County',
      titleEs: 'Entrega Rápida',
      descriptionEs: 'Entrega disponible en todo el Condado de Montgomery'
    },
    {
      icon: Leaf,
      title: 'Fresh Cut from the Farm',
      description: 'Top-grade grass fresh cut from the farm, plus quality materials',
      titleEs: 'Fresco del Campo',
      descriptionEs: 'Pasto de primera calidad recién cortado del campo, más materiales de calidad'
    },
    {
      icon: Award,
      title: 'Expert Service',
      description: 'Professional landscaping expertise and guidance',
      titleEs: 'Servicio Experto',
      descriptionEs: 'Experiencia profesional en jardinería y asesoramiento'
    },
    {
      icon: Users,
      title: 'Family Owned',
      description: 'Local family business serving the community since 2025',
      titleEs: 'Negocio Familiar',
      descriptionEs: 'Negocio familiar local sirviendo a la comunidad desde 2025'
    }
  ];

  const categories = [
    {
      name: language === 'en' ? 'Grass' : 'Pasto',
      description: language === 'en' ? 'Premium sod varieties for Texas lawns' : 'Variedades premium de césped para jardines de Texas',
      href: `/${language}/grass`,
      image: grassVarietiesImage,
      color: 'from-green-500 to-green-600'
    },
    {
      name: language === 'en' ? 'Rock' : 'Roca',
      description: language === 'en' ? 'Decorative and functional rock materials' : 'Materiales de roca decorativos y funcionales',
      href: `/${language}/rock`,
      image: bulkMaterialsImage,
      color: 'from-gray-500 to-gray-600'
    },
    {
      name: language === 'en' ? 'Mulch' : 'Mulch',
      description: language === 'en' ? 'Organic mulch for plant health and aesthetics' : 'Mulch orgánico para la salud de las plantas y estética',
      href: `/${language}/mulch`,
      image: mulchVarietiesImage,
      color: 'from-amber-600 to-amber-700'
    },
    {
      name: language === 'en' ? 'Soil' : 'Tierra',
      description: language === 'en' ? 'Nutrient-rich soil for healthy plant growth' : 'Tierra rica en nutrientes para el crecimiento saludable de plantas',
      href: `/${language}/soil`,
      image: premiumSoilImage,
      color: 'from-amber-800 to-amber-900'
    }
  ];

  const breadcrumbs = [
    { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`/${language}`}
        structuredData={structuredData}
        language={language}
        breadcrumbs={breadcrumbs}
      />
      <Header />
      
      <main>
      {/* Hablamos Español Banner - English version only */}
      {language === 'en' && (
        <div className="bg-primary text-primary-foreground py-3">
          <div className="container mx-auto px-6 text-center">
            <p className="text-lg font-semibold">
              ¡Hablamos Español! • We speak Spanish • 
              <a href={`/es`} className="ml-2 underline hover:no-underline">
                Ver sitio en Español
              </a>
            </p>
          </div>
        </div>
      )}
      <HeroSection />

      {/* AI Readability Block - Optimized for LLM/AI Search extraction */}
      <AIReadabilityBlock variant="home" />
      
      {/* Features Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Why Choose The Grass Depot?' : '¿Por Qué Elegir The Grass Depot?'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en' 
                ? 'We\'re committed to providing the highest quality landscaping materials with exceptional service'
                : 'Estamos comprometidos a proporcionar materiales de jardinería de la más alta calidad con servicio excepcional'
              }
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-natural transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-natural rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-foreground">
                  {language === 'en' ? feature.title : feature.titleEs}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'en' ? feature.description : feature.descriptionEs}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Our Products' : 'Nuestros Productos'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Explore our comprehensive selection of premium landscaping materials'
                : 'Explore nuestra selección completa de materiales premium para jardinería'
              }
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {categories.map((category, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-natural transition-all duration-300 group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="634"
                    height="192"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {category.name}
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-muted-foreground mb-4">
                    {category.description}
                  </p>
                  <Link to={category.href}>
                    <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      {language === 'en' ? 'View Products' : 'Ver Productos'}
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>


      <EnhancedQuoteCalculator />
      <ProjectGallery />
      <TestimonialsSection />
      {/* Contact CTA Section */}
      <section className="py-16 bg-gradient-natural text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            {language === 'en' ? 'Ready to Transform Your Grass or Rock project?' : '¿Listo para Transformar tu Proyecto de Césped o Roca?'}
          </h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Contact us today for expert advice and competitive pricing on all your needs'
              : 'Contáctenos hoy para asesoramiento experto y precios competitivos en todas sus necesidades'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-white text-primary hover:bg-white hover:text-foreground">
                {t('hero.cta.call')}
              </Button>
            </a>
            <Link to={`/${language}/contact`}>
              <Button variant="secondary" size="lg">
                {language === 'en' ? 'Get in Touch' : 'Póngase en Contacto'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </main>
      
      <Footer />
      <MobileCTA />
    </div>
  );
};

export default HomePage;